import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, endpoints } from '../api';

export interface Amenity {
  id: number;
  name: string;
  icon?: string;
  category?: string;
  description?: string;
  is_popular?: boolean;
}

export interface Hotel {
  id: number;
  name: string;
  summary?: string;
  description?: string;
  country_id: number;
  country?: {
    id: number;
    name: string;
    slug: string;
  };
  hotel_type_id?: number;
  hotel_type?: {
    id: number;
    name: string;
    slug: string;
  };
  stars?: number;
  address?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  price_category?: string;
  amenity_ids?: number[];
  check_in_time?: string;
  check_out_time?: string;
  image_id?: string;
  image_url?: string;
  cover_image?: string;
  slug: string;
  tags?: Array<{
    id: number;
    name: string;
    slug: string;
    category?: string;
    color?: string;
    icon?: string;
  }>;
  tag_ids?: number[];
  is_active: boolean;
  is_featured: boolean;
  order_index?: number;
  price_charts?: any[];
  lowestPrice?: number | null;
  created_at: string;
  updated_at: string;
}

export interface HotelCreateInput {
  name: string;
  summary?: string;
  description?: string;
  country_id: number;
  hotel_type_id?: number;
  stars?: number;
  address?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  price_category?: string;
  amenity_ids?: number[];
  tag_ids?: number[];
  check_in_time?: string;
  check_out_time?: string;
  image_id?: string;
}

export interface HotelUpdateInput extends Partial<HotelCreateInput> {
  is_active?: boolean;
  is_featured?: boolean;
}

export interface HotelRelationships {
  package_ids: number[];
  group_trip_ids: number[];
}

export interface PaginatedHotelResponse {
  items: Hotel[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Hook for fetching paginated hotels (optimized for Admin List)
export const usePaginatedHotels = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  country_id?: number;
  tag?: string;
  include_inactive?: boolean;
  order_by?: string;
  order?: string;
}) => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
  }

  const queryString = queryParams.toString();
  const endpoint = queryString ? `${endpoints.hotels.paginated(queryString)}` : endpoints.hotels.paginated();

  return useQuery<PaginatedHotelResponse>({
    queryKey: ['hotels', 'paginated', params],
    queryFn: async () => {
      return apiClient.get<PaginatedHotelResponse>(endpoint);
    },
  });
};

// Hook for fetching hotels
export const useHotels = (countryId?: number) => {
  return useQuery<Hotel[]>({
    queryKey: ['hotels', countryId],
    queryFn: async () => {
      const endpoint = countryId
        ? endpoints.hotels.byCountry(countryId)
        : endpoints.hotels.list();
      const response = await apiClient.get<Hotel[]>(endpoint);
      return response;
    }
  });
};

// Hook for fetching a single hotel by ID
export const useHotel = (id: number) => {
  return useQuery<Hotel>({
    queryKey: ['hotels', id],
    queryFn: async () => {
      const response = await apiClient.get<Hotel>(endpoints.hotels.detail(id));
      return response;
    },
    enabled: !!id,
  });
};

// Extended hotel interface for detailed view
export interface HotelDetails extends Hotel {
  cover_image?: string;
  gallery_images?: Array<{
    id: number;
    filename: string;
    alt_text?: string;
    title?: string;
    caption?: string;
    width?: number;
    height?: number;
    file_path: string;
  }>;
  amenities?: Amenity[];
  price_charts?: any[];
}

// Hook for fetching hotel details by slug
export const useHotelBySlug = (slug: string) => {
  return useQuery<HotelDetails>({
    queryKey: ['hotels', 'slug', slug],
    queryFn: async () => {
      const response = await apiClient.get<HotelDetails>(endpoints.hotels.detailsBySlug(slug));
      return response;
    },
    enabled: !!slug,
  });
};

// Hook for creating a new hotel
export const useCreateHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hotel: HotelCreateInput) => {
      const response = await apiClient.post(endpoints.hotels.create(), hotel);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotels'] });
    },
  });
};

// Hook for updating a hotel
export const useUpdateHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...hotel }: HotelUpdateInput & { id: number }) => {
      const response = await apiClient.put(`/hotels/${id}`, hotel);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hotels'] });
      queryClient.invalidateQueries({ queryKey: ['hotels', variables.id] });
    },
  });
};

// Hook for deleting a hotel
export const useDeleteHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiClient.delete(`/hotels/${id}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotels'] });
    },
  });
};

// Hook for fetching hotel's relationships
export const useHotelRelationships = (id: number) => {
  return useQuery<HotelRelationships>({
    queryKey: ['hotels', id, 'relationships'],
    queryFn: async () => {
      const response = await apiClient.get<HotelRelationships>(`/hotels/${id}/relationships`);
      return response;
    },
    enabled: !!id,
  });
};

// Hook for assigning a package to a hotel
export const useAssignPackageToHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hotelId, packageId }: { hotelId: number; packageId: number }) => {
      const response = await apiClient.post(`/hotels/${hotelId}/packages/${packageId}`, {});
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hotels', variables.hotelId, 'relationships'] });
    },
  });
};

// Hook for removing a package from a hotel
export const useRemovePackageFromHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hotelId, packageId }: { hotelId: number; packageId: number }) => {
      const response = await apiClient.delete(`/hotels/${hotelId}/packages/${packageId}`);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hotels', variables.hotelId, 'relationships'] });
    },
  });
};

// Hook for assigning a group trip to a hotel
export const useAssignGroupTripToHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hotelId, groupTripId }: { hotelId: number; groupTripId: number }) => {
      const response = await apiClient.post(`/hotels/${hotelId}/group-trips/${groupTripId}`, {});
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hotels', variables.hotelId, 'relationships'] });
    },
  });
};

// Hook for removing a group trip from a hotel
export const useRemoveGroupTripFromHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hotelId, groupTripId }: { hotelId: number; groupTripId: number }) => {
      const response = await apiClient.delete(`/hotels/${hotelId}/group-trips/${groupTripId}`);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hotels', variables.hotelId, 'relationships'] });
    },
  });
};

// Hook for reordering hotels (admin only)
export const useReorderHotels = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orders: { id: number; order_index: number }[]) => {
      return apiClient.patch('/hotels/reorder', { orders });
    },
    // Returning the promise makes mutateAsync wait until fresh data is loaded (no order flicker)
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['hotels'], exact: false }),
  });
};
